<script lang="ts" setup>import { computed as _computed, ref as _ref } from 'vue';

import { watchDebounced } from '@vueuse/core'
import GiftRecommendationCard from '@consumer/modules/myAccount/shared/recommendations/GiftRecommendationCard.vue'
import MultiplaceRecommendationsSection
  from '@consumer/modules/myAccount/shared/recommendations/MultiplaceRecommendationsSection.vue'
import RecommendationsSection from '@consumer/modules/myAccount/shared/recommendations/RecommendationsSection.vue'
import RecommendationsSwiper from '@consumer/modules/myAccount/shared/recommendations/RecommendationsSwiper.vue'
import { giftRecommendations, locations as locationsApi } from '@consumer/api'
import { dedupeGifts, giftDedupeKey } from '@consumer/utils/giftDedupe'
import { useGiftBadging, type RecommendedGift } from '@consumer/composables/giftBadging'
import { signalFilled, useConciergeState, type SignalValues } from '@consumer/modules/buyersConcierge/conciergeState'
import { hasUnplacedContent, matchSignals } from '@consumer/modules/buyersConcierge/parseSignals'
import { recommendationReadiness } from '@consumer/modules/buyersConcierge/recommendationRequest'
import {
  fetchKey,
  recoveredToApply,
  shouldFetch,
  shouldRefine,
} from '@consumer/modules/buyersConcierge/searchTriggers'
import { trackConciergeEvent, conciergeQueryMetadata } from '@consumer/modules/buyersConcierge/conciergeEventTracking'
import type { ConciergeInputVariantName } from '@consumer/modules/buyersConcierge/ConciergeInputVariant.vue'

// Rich, event-page-parity results for the Buyer's Concierge, rendered directly by
// the concierge takeover. Driven by the concierge store's signals rather than a
// saved subject; the detected-signal chips above (in ConciergeInput) already
// summarize the basis.
type Gift = RecommendedGift

withDefaults(defineProps<{ variant?: ConciergeInputVariantName }>(), { variant: 'free_text', })

const state = useConciergeState()

const PAGE_SIZE = 18

// The signal slots the AI refine can fill; budget is excluded (we never nudge a price).
const REFINABLE_SIGNALS = ['relationship', 'occasion', 'location', 'interests'] as const

const readiness = _computed(() => recommendationReadiness(state.signals))

// The engine's locality section keys off a canonical "City, ST" (its saved-subject
// callers get that from the location picker); the concierge parses free text to a
// bare city, so resolve it through the same featured-city autocomplete before it
// drives the query — otherwise the local section never matches. Already-canonical
// input ("Seattle, WA") passes through untouched.
let resolvedLocation = _ref<string | null>(null)

// Seq guard: the search is async, so a slower earlier city must not overwrite a
// newer one (or a since-cleared field). Bumped on every call so an in-flight
// search whose input is stale drops its result.
let resolveSeq = 0

async function resolveLocation (city: string | null) {
  const seq = ++resolveSeq
  if (!city) {
    resolvedLocation.value = null
    return
  }
  // Only skip resolution for the exact canonical "City, ST" the backend matches
  // on (comma-space + uppercase state); anything looser is normalized by the search.
  if (/, [A-Z]{2}$/.test(city)) {
    resolvedLocation.value = city
    return
  }
  try {
    const matches = await locationsApi.search<{ value: string }[]>({ data: { term: city } })
    if (seq !== resolveSeq) return
    resolvedLocation.value = matches?.[0]?.value ?? null
  }
  catch {
    if (seq === resolveSeq) resolvedLocation.value = null
  }
}

watch(() => state.signals.location.value, resolveLocation, { immediate: true })

// snake_case keys: sent straight through as query params to the concierge source.
const conciergeQuery = _computed(() => ({
  recommendation_source: 'concierge',
  relationship: state.signals.relationship.value ?? undefined,
  occasion: state.signals.occasion.value ?? undefined,
  interests: state.signals.interests.value,
  location: resolvedLocation.value ?? undefined,
}))

const interests = _computed(() => state.signals.interests.value)
const locations = _computed(() => (resolvedLocation.value ? [resolvedLocation.value] : []))

const { badgesFor, resolveLocationImages } = useGiftBadging({
  interests: (interests),
  locations: (locations),
})

let gifts = _ref<Gift[]>([])
let loading = _ref(false)
let loadingMore = _ref(false)
let hasMore = _ref(false)
let nextOffset = _ref(0)

// Bump to re-key (and thus refetch) the self-fetching local sections when the
// signals change under them.
let placesKey = _ref(0)

// Drops a stale/superseded response so a slower earlier fetch can't overwrite a
// newer one — and so an in-flight fetch can't land after the field is cleared.
// Mirrors ConciergeInput's parseSeq guard.
let fetchSeq = 0

let resultsRefinable = false

async function fetchGifts ({ append = false } = {}) {
  if (append && (loadingMore.value || !hasMore.value)) return

  const seq = ++fetchSeq
  const requestedOffset = append ? nextOffset.value : 0
  if (append) loadingMore.value = true
  else loading.value = true

  try {
    const response = await giftRecommendations.index<{
      gifts: Gift[]
      hasMore?: boolean
      nextOffset?: number
      refinable?: boolean
    }>({
      query: {
        ...conciergeQuery.value,
        section: 'nationwide',
        limit: PAGE_SIZE,
        offset: requestedOffset,
      },
    })
    if (seq !== fetchSeq) return

    const fetched = response.gifts ?? []
    gifts.value = append ? dedupeGifts([...gifts.value, ...fetched]) : fetched
    hasMore.value = response.hasMore ?? false
    // Advance by the server window (offset requested + items returned), not the
    // deduped client length — dedupe can shrink `gifts` below the server offset and
    // cause a page to be re-requested.
    nextOffset.value = response.nextOffset ?? (requestedOffset + fetched.length)
    if (!append) resultsRefinable = response.refinable ?? false
  }
  finally {
    // Always release `loadingMore` — only one append is ever in flight, so a
    // superseded one must still clear it or pagination stays blocked. `loading`
    // stays owned by the latest fetch, so only clear it when this call is latest.
    if (append) loadingMore.value = false
    else if (seq === fetchSeq) loading.value = false
  }
}

let lastRefinedText = ''

// AI-recover the signals the deterministic parser missed, then merge them into the
// store as `inferred` (never overriding what the buyer stated). The store change
// re-drives the normal fetch, so the cards, the detected chips, and the local
// sections all update through one path — no bespoke swap.
async function refineSignals (raw: string) {
  lastRefinedText = raw
  try {
    const response = await giftRecommendations.refine<{ signals?: Partial<SignalValues> }>({
      data: { recommendation_source: 'concierge', raw },
    })
    // Clearing the marker lets the newer text refine on its next pause even when
    // the edit didn't add words.
    if (raw !== state.raw.trim()) {
      lastRefinedText = ''
      return
    }

    state.setRecovered(recoveredToApply(response.signals ?? {}))
  }
  catch {
    // Let the next pause retry; the deterministic results already stand.
    lastRefinedText = ''
  }
}

// The dedup key of the last fetch (see searchTriggers.fetchKey).
let fetchedQueryKey = ''

// Watches the raw text too, so the long-input fallback fires even when nothing parsed
// and `conciergeQuery` never changed. No `immediate`: ConciergeInput re-parses the
// persisted text on mount, which moves the signals and triggers this once — an
// immediate fire would just duplicate it.
watchDebounced(
  () => [JSON.stringify(conciergeQuery.value), state.raw] as const,
  () => {
    if (!shouldFetch(state.raw, readiness.value.ready)) return

    const queryKey = fetchKey(readiness.value.ready, JSON.stringify(conciergeQuery.value), state.raw)
    if (queryKey === fetchedQueryKey) return
    fetchedQueryKey = queryKey
    placesKey.value++
    resolveLocationImages()
    // Clear the key on failure so a transient error doesn't permanently suppress a
    // retry — and so the unawaited fetch can't reject unhandled.
    fetchGifts().catch(() => { fetchedQueryKey = '' })
  },
  { debounce: 500 },
)

// Refine cadence is deliberately separate from — and much slower than — the fetch. An
// AI call fires only after a real typing pause AND once the buyer has added words since
// the last parse AND the current results still came back thin. That throttles the calls
// (no per-keystroke firing) and keeps re-checking as the buyer writes more, instead of
// going quiet after the first parse.
const REFINE_PAUSE_MS = 3000

watchDebounced(
  () => state.raw,
  () => {
    const inputs = {
      resultsRefinable,
      hasUnmatched: hasUnplacedContent(matchSignals(state.raw).unmatched),
      hasGap: REFINABLE_SIGNALS.some(name => !signalFilled(state.signals[name])),
      lastRefinedText,
    }
    if (!shouldRefine(state.raw, inputs)) return
    refineSignals(state.raw.trim())
  },
  { debounce: REFINE_PAUSE_MS },
)

// Emptying the field resets everything; short of that we keep the last results on
// screen while a new fetch runs, so cards never blink out mid-typing. Bump both seq
// guards so an in-flight fetch or city resolution can't land after the clear, and
// reset the paging flags so pagination isn't left stuck.
watch(() => state.raw, (raw) => {
  if (!raw.trim()) {
    fetchSeq++
    resolveSeq++
    fetchedQueryKey = ''
    lastRefinedText = ''
    resultsRefinable = false
    gifts.value = []
    loading.value = false
    loadingMore.value = false
    hasMore.value = false
    nextOffset.value = 0
    resolvedLocation.value = null
  }
})

// Per-card viewport impressions are deliberately not tracked.
watch(() => loading.value, (isLoading, wasLoading) => {
  if (!wasLoading || isLoading) return
  // Clearing/cancelling the field forces loading→false (and blanks fetchedQueryKey);
  // only emit results events for a real fetch completion, not a reset.
  if (!fetchedQueryKey) return
  const query = conciergeQueryMetadata(__props.variant, state.raw, state.signals)
  if (gifts.value.length > 0 || locations.value.length > 0) {
    const readyMeta = { gifts: gifts.value.length, locations: locations.value.length, ...query }
    trackConciergeEvent('results_ready', { variant: __props.variant, metadata: readyMeta })
  }
  else {
    trackConciergeEvent('results_empty', { variant: __props.variant, metadata: query })
  }
})

// Delegated so it covers every card (swiper + local sections) without touching the shared
// GiftRecommendationCard.
function onResultClick (event: MouseEvent) {
  const target = event.target as HTMLElement | null
  const anchor = target?.closest('a[name="calendar-gift-recommendation"]') ?? null
  if (!anchor) return

  const section = anchor.closest('[data-concierge-section]')
  const cards = section ? Array.from(section.querySelectorAll('a[name="calendar-gift-recommendation"]')) : []
  trackConciergeEvent('result_click', {
    variant: __props.variant,
    metadata: {
      href: anchor.getAttribute('href'),
      section: section?.getAttribute('data-concierge-section') ?? 'unknown',
      position: cards.indexOf(anchor),
    },
  })
}
</script>

<template>
  <div v-if="readiness.ready || gifts.length > 0" class="concierge-recommendations" @click="onResultClick">
    <MultiplaceRecommendationsSection
      v-for="location in locations"
      :key="`${placesKey}-local-${location}`"
      data-concierge-section="local"
      :query="conciergeQuery"
      :location="location"
      :title="`Multi-Place Gifts in ${location}`"
      :badgesFor="badgesFor"
    />

    <RecommendationsSection
      v-if="loading || gifts.length > 0"
      data-concierge-section="nationwide"
      title="Popular Multi-Place Gifts"
    >
      <RecommendationsSwiper
        :items="gifts"
        :itemKey="giftDedupeKey"
        :loading="loading"
        :loadingMore="loadingMore"
        @nearEnd="fetchGifts({ append: true })"
      >
        <template #skeleton>
          <GiftRecommendationCard loading/>
        </template>

        <template #default="{ item }">
          <GiftRecommendationCard :idea="item" :badges="badgesFor(item)"/>
        </template>
      </RecommendationsSwiper>
    </RecommendationsSection>

    <p v-if="!loading && !gifts.length && !locations.length" class="concierge-recommendations__empty">
      No matches yet — try another detail.
    </p>
  </div>
</template>

<style lang="scss" scoped>
.concierge-recommendations {
  display: grid;
  gap: 8px;
  margin-top: 16px;
  width: 100%;
}

.concierge-recommendations__empty {
  color: var(--color-text-tertiary);
  font-size: 14px;
  margin: 8px 0 0;
}
</style>
